import { builder } from "@builder.io/react";
import { select, take, put, takeEvery } from "redux-saga/effects";
import { isEmpty, find } from "lodash";
import queryString from "query-string";
import * as Shop from "../types/Shop";
import { REHYDRATION_COMPLETE } from "../types/App";
import {
  updateQuantityRequest,
  removeFromCartRequest,
  applyDiscountCodeRequest,
  removeDiscountCodeRequest,
  updateCheckoutRequest,
  fetchCheckoutRequest,
  createCheckoutRequest,
  updateLineItemsRequest,
} from "../actions/ShopActions";
import Logger from "../utils/logger";
import { BUILDER_CHECKOUT_SESSION_ID_KEY } from "../utils/constants";
import { RESTORE_MEMBERSHIP_BUNDLE_KEY } from "../utils/productConstants";
import {
  getCheckoutId,
  getCartLineItems,
  getOrderedLineItems,
  getRouter,
  getCheckoutCustomAttributes,
  getCheckoutState,
  getAllProducts,
} from "../utils/reduxSelectors";
import {
  isInCart,
  isDiscountCodeApplied,
  discountsArePresent,
  getVariantData,
  getLineItemsWithFulfillmentAttribute,
  getProductsWithoutFulfillmentAttribute,
  updateLineItemsWithFulfillmentServiceAttribute,
} from "../utils/shop";
import { constructUrlWithQueryParams } from "../utils/general";
import {
  getAutoApplyDiscountCode,
  isAutoApplyDiscountCodeEnabled,
} from "../utils/promos";

// Constants
const TAG = "ShopSaga";
const IS_AUTO_APPLY_DISCOUNT_FEATURE_ENABLED = isAutoApplyDiscountCodeEnabled();
const AUTO_APPLY_DISCOUNT_CODE = getAutoApplyDiscountCode();

const ShopSaga = () => {
  function* worker(action) {
    Logger.d(TAG, `Processing action: ${action.type}`);

    const checkoutId = yield select(getCheckoutId) || null;
    const invalidCheckoutExists =
      !checkoutId || action.type === Shop.STALE_CHECKOUT_ID;

    // Create a new checkout if no checkout exists OR
    // the current checkout has been completed OR
    // the checkout ID is stale and invalid
    if (invalidCheckoutExists) {
      const checkoutInput = builder.sessionId
        ? {
            customAttributes: [
              {
                key: BUILDER_CHECKOUT_SESSION_ID_KEY,
                value: builder.sessionId,
              },
            ],
          }
        : {};

      Logger.d(TAG, "Creating new checkout...");

      yield put(createCheckoutRequest(checkoutInput));

      const { errors, type } = yield take([
        Shop.CREATE_CHECKOUT_RECEIVE,
        Shop.CREATE_CHECKOUT_REQUEST_FAILURE,
      ]);

      if (type === Shop.CREATE_CHECKOUT_REQUEST_FAILURE) {
        return Logger.e(
          TAG,
          `Create checkout error: ${JSON.stringify(errors)}`
        );
      }

      Logger.d(TAG, "New checkout created...");
    }

    /**
     *  On page load:
     *    - Fetch checkout
     *    - If no checkout, a completed checkout, or a stale checkout is returned,
     *      we need to create a new checkout
     *    - Once a valid checkout is in place, then we can proceed.
     *
     */
    if (action.type === REHYDRATION_COMPLETE && checkoutId) {
      // Check the current checkout to see if a valid one exists
      Logger.d(TAG, "Checking checkout...");

      let fetchCheckoutResult = null;

      yield put(fetchCheckoutRequest(checkoutId));

      fetchCheckoutResult = yield take([
        Shop.FETCH_CHECKOUT_RECEIVE,
        Shop.FETCH_CHECKOUT_REQUEST_FAILURE,
      ]);

      if (fetchCheckoutResult.type === Shop.FETCH_CHECKOUT_REQUEST_FAILURE) {
        return Logger.e(
          TAG,
          `Fetch checkout error: ${JSON.stringify(fetchCheckoutResult.errors)}`
        );
      }

      // If checkoutId exists and builder.sessionId exists and
      // no builder.sessionId exists on the checkout object,
      // update checkout attributes to add sessionId
      const checkoutAttributes = yield select(getCheckoutCustomAttributes);
      const hasBuilderSessionId = checkoutAttributes.some(
        ({ key }) => key === BUILDER_CHECKOUT_SESSION_ID_KEY
      );

      // If no builder session ID is present on the current checkout
      // object AND there is a builder session ID present,
      // update the current checkout object to include the Builder
      // session ID as part of its custom attributes
      if (!hasBuilderSessionId && builder.sessionId) {
        yield put(
          updateCheckoutRequest(checkoutId, {
            customAttributes: [
              {
                key: BUILDER_CHECKOUT_SESSION_ID_KEY,
                value: builder.sessionId,
              },
            ],
          })
        );

        // Wait for the result
        const updateCheckoutResult = yield take([
          Shop.UPDATE_CHECKOUT_RECEIVE,
          Shop.UPDATE_CHECKOUT_REQUEST_FAILURE,
        ]);

        if (
          updateCheckoutResult.type === Shop.UPDATE_CHECKOUT_REQUEST_FAILURE
        ) {
          return Logger.e(
            TAG,
            `Update checkout error: ${JSON.stringify(
              updateCheckoutResult.errors
            )}`
          );
        }
      }
    }

    // PRIME DAY
    //  1) On fresh page load + when valid checkout already exists
    //  2) When a stale checkout OR no checkout exists and new checkout is created
    //  3) A user is already on the page and adds an item to cart
    //  4) A user already is already on the site,
    //     has the Restore bundle in their cart, and clicks Checkout.
    //
    //  All of these use cases need to be addressed as we need to reflect
    //  the discounted price for the bundle in the cart.
    //  If the bundle is already in the cart, this will
    const autoApplyDiscountEvents =
      action.type === REHYDRATION_COMPLETE || // Fresh page load
      action.type === Shop.CREATE_CHECKOUT_RECEIVE || // Fetch new checkout
      action.type === Shop.ADD_TO_CART_RECEIVE || // Add item to cart
      action.type === Shop.CHECKOUT; // Proceed with checkout

    if (IS_AUTO_APPLY_DISCOUNT_FEATURE_ENABLED && AUTO_APPLY_DISCOUNT_CODE) {
      const checkout = action.checkout || (yield select(getCheckoutState));
      const orderedLineItems = yield select(getOrderedLineItems);
      const restoreBundleData = (yield select(getAllProducts))[
        RESTORE_MEMBERSHIP_BUNDLE_KEY
      ];
      const { id } = getVariantData(restoreBundleData);
      const restoreBundleIsInCart = isInCart(id, orderedLineItems);

      if (
        restoreBundleIsInCart &&
        checkout &&
        !isDiscountCodeApplied(checkout, AUTO_APPLY_DISCOUNT_CODE) &&
        autoApplyDiscountEvents
      ) {
        yield put(
          applyDiscountCodeRequest(checkout.id, AUTO_APPLY_DISCOUNT_CODE)
        );

        const autoApplyDiscountCodeResult = yield take([
          Shop.APPLY_DISCOUNT_CODE_RECEIVE,
          Shop.APPLY_DISCOUNT_CODE_FAILURE,
        ]);

        if (
          autoApplyDiscountCodeResult.type === Shop.APPLY_DISCOUNT_CODE_FAILURE
        ) {
          return Logger.e(
            TAG,
            `Auto apply discount code error: ${JSON.stringify(
              autoApplyDiscountCodeResult.errors
            )}`
          );
        }
      }
    }

    /**
     * When to auto-apply if coupon isn't present already
     *  - On fresh page load + valid checkout object
     *  - On fresh page load + stale checkout object
     *  - Person is already on a page and adds to cart
     *
     */

    /**
     * We run applyDiscountCodeRequest when we have a valid checkoutId.
     * To check if we have a valid checkoutId, we do the following:
     *  1. Check if `checkoutId` exists AND we are receiving data from fetching a checkout AND it's not a completed
     *  checkout OR
     *  2. Check if we just created a new `checkout` object
     *
     * We set the discountCode to be either the auto-applied code set in the environment variables or
     * the "coupon" URL search param if present.
     */
    const { location } = yield select(getRouter);
    const { coupon } = queryString.parse(location.search);

    if (
      coupon &&
      (action.type === Shop.CREATE_CHECKOUT_RECEIVE ||
        (checkoutId &&
          action.type === Shop.FETCH_CHECKOUT_RECEIVE &&
          !action.checkout.completedAt)) &&
      !isDiscountCodeApplied(action.checkout, coupon)
    ) {
      yield put(applyDiscountCodeRequest(checkoutId, coupon));
      const applyDiscountCodeResults = yield take([
        Shop.APPLY_DISCOUNT_CODE_RECEIVE,
        Shop.APPLY_DISCOUNT_CODE_FAILURE,
      ]);

      if (applyDiscountCodeResults.type === Shop.APPLY_DISCOUNT_CODE_FAILURE) {
        return Logger.d(
          TAG,
          `Apply discount code error: ${JSON.stringify(
            applyDiscountCodeResults.errors
          )}`
        );
      }
    }

    const checkout = action.checkout || (yield select(getCheckoutState));

    if (
      discountsArePresent(checkout) &&
      ((autoApplyDiscountEvents &&
        !IS_AUTO_APPLY_DISCOUNT_FEATURE_ENABLED &&
        isDiscountCodeApplied(checkout, AUTO_APPLY_DISCOUNT_CODE)) ||
        action.type === Shop.INVALID_DISCOUNT_CODE_APPLIED)
    ) {
      yield put(removeDiscountCodeRequest(checkoutId));

      const removeDiscountCodeResults = yield take([
        Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
        Shop.REMOVE_DISCOUNT_CODE_FAILURE,
      ]);

      if (
        removeDiscountCodeResults.type === Shop.REMOVE_DISCOUNT_CODE_FAILURE
      ) {
        return Logger.d(
          TAG,
          `Removing discount code error: ${JSON.stringify(
            removeDiscountCodeResults.errors
          )}`
        );
      }
    }

    // Dispatch quantity increase action instead if "add to cart" button was clicked
    // for an item that already is in the cart
    if (action.type === Shop.ADD_TO_CART_REQUEST) {
      Logger.d(TAG, "Checking for item in existing cart...");

      const orderedLineItems = yield select(getOrderedLineItems);
      const cartItemMatch = orderedLineItems.includes(action.variantId);

      if (cartItemMatch) {
        const checkoutId = yield select(getCheckoutId);
        const lineItems = yield select(getCartLineItems);
        const { id, quantity } = find(
          lineItems,
          (item) => item.variant.id === action.variantId
        );
        yield put(updateQuantityRequest(checkoutId, id, quantity + 1));

        // Wait for the result
        const getUpdateQuantityResult = yield take([
          Shop.UPDATE_QUANTITY_RECEIVE,
          Shop.UPDATE_QUANTITY_REQUEST_FAILURE,
        ]);

        if (
          getUpdateQuantityResult.type === Shop.FETCH_CHECKOUT_REQUEST_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Get quantity update error: ${JSON.stringify(
              getUpdateQuantityResult.errors
            )}`
          );
        }
      }
    }

    // Dispatch remove from cart action instead if the new quantity for a line item
    // after decrementing it is now 0. This is to ensure that the item is also
    // removed from our orderedLineItems list.
    if (action.type === Shop.UPDATE_QUANTITY_REQUEST && action.quantity === 0) {
      Logger.d(TAG, "Checking updated line item quantity...");

      const lineItems = yield select(getCartLineItems);

      const { checkoutId, lineItemId } = action;
      const { variant } = find(lineItems, (item) => item.id === lineItemId);
      yield put(removeFromCartRequest(checkoutId, lineItemId, variant.id));

      // Wait for the result
      const getRemoveFromCartResult = yield take([
        Shop.REMOVE_FROM_CART_RECEIVE,
        Shop.REMOVE_FROM_CART_REQUEST_FAILURE,
      ]);

      if (
        getRemoveFromCartResult.type === Shop.REMOVE_FROM_CART_REQUEST_FAILURE
      ) {
        return Logger.d(
          TAG,
          `Removal from cart failed: ${JSON.stringify(
            getRemoveFromCartResult.errors
          )}`
        );
      }
    }

    if (action.type === Shop.CHECKOUT) {
      const lineItems = yield select(getCartLineItems);
      const amazonFulfilledLineItems =
        getLineItemsWithFulfillmentAttribute(lineItems);

      if (!isEmpty(amazonFulfilledLineItems)) {
        const lineItemsWithoutFulfillmentAttribute =
          getProductsWithoutFulfillmentAttribute(amazonFulfilledLineItems);

        if (!isEmpty(lineItemsWithoutFulfillmentAttribute)) {
          const updatedLineItems =
            updateLineItemsWithFulfillmentServiceAttribute(
              lineItemsWithoutFulfillmentAttribute
            );

          Logger.d(TAG, "Updating checkout line items...");

          yield put(
            updateLineItemsRequest({ lineItems: updatedLineItems, checkoutId })
          );

          const updateLineItemsResult = yield take([
            Shop.UPDATE_LINE_ITEMS_RECEIVE,
            Shop.UPDATE_LINE_ITEMS_FAILURE,
          ]);
          if (updateLineItemsResult.type === Shop.UPDATE_LINE_ITEMS_FAILURE) {
            return Logger.d(
              TAG,
              `Update line items error: ${JSON.stringify(
                updateLineItemsResult.errors
              )}`
            );
          }
        }
      }

      const {
        location: { search },
      } = yield select(getRouter);
      const query = builder.sessionId
        ? `${search}&builder.overrideSessionId=${builder.sessionId}`
        : search;
      const checkoutUrl = constructUrlWithQueryParams(action.webUrl, query);
      // Fix to manually create GA cross-domain linker query parameter
      // https://www.andrewdixon.co.uk/2019/09/22/manually-decorate-a-link-with-google-analytics-cross-domain-measure-query-parameter-gtag-js/
      if (window && window.ga && window.gaplugins) {
        const tracker = window.ga.getAll()[0];
        const linker = new window.gaplugins.Linker(tracker);
        const gaDecoratedCheckoutUrl = linker.decorate(checkoutUrl);

        window.location = gaDecoratedCheckoutUrl;

        return;
      }

      window.location = checkoutUrl;
    }
  }

  function* watcher() {
    yield takeEvery(
      [
        REHYDRATION_COMPLETE,
        Shop.FETCH_CHECKOUT_RECEIVE,
        Shop.CREATE_CHECKOUT_RECEIVE,
        Shop.ADD_TO_CART_REQUEST,
        Shop.ADD_TO_CART_RECEIVE,
        Shop.UPDATE_QUANTITY_REQUEST,
        Shop.INVALID_DISCOUNT_CODE_APPLIED,
        Shop.REMOVE_FROM_CART_RECEIVE,
        Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
        Shop.CHECKOUT,
        Shop.STALE_CHECKOUT_ID,
      ],
      worker
    );
  }

  return {
    watcher,
    worker,
  };
};

export default ShopSaga;
